import { useState, useCallback } from 'react';
import { Button } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { useActionExecution } from '../../hooks/useActionExecution';
import type { FormConfirmationAction, AssistantMessage } from '../../types';
import styles from './ActionCard.module.css';

interface Props {
  action: FormConfirmationAction;
  message: AssistantMessage;
}

export default function FormConfirmationCard({ action, message }: Props) {
  const [confirmed, setConfirmed] = useState(action.executed ?? false);
  const { mutateAsync, isLoading } = useActionExecution();

  const handleConfirm = useCallback(async () => {
    try {
      await mutateAsync({
        correlationId: message.correlationId,
        messageId: message.messageId,
        action: action.confirmAction,
        payload: action as unknown as Record<string, unknown>,
      });
      setConfirmed(true);
    } catch {
      // Error handling — could show notification
    }
  }, [mutateAsync, message, action]);

  if (confirmed) {
    return (
      <div className={styles.confirmed}>
        <div className={styles.successMessage}>
          <FontAwesomeIcon icon={faCircleCheck} />
          {action.confirmLabel} completed
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      {action.title && <div className={styles.cardTitle}>{action.title}</div>}

      {action.diaryNote && (
        <div>
          <div className={styles.cardSubtitle}>{action.diaryNote.category}</div>
          <p
            style={{ fontSize: 'var(--mantine-font-size-xs)', margin: '4px 0' }}
          >
            {action.diaryNote.body}
          </p>
        </div>
      )}

      {action.note && (
        <div>
          <div className={styles.cardSubtitle}>{action.note.title}</div>
          <p
            style={{ fontSize: 'var(--mantine-font-size-xs)', margin: '4px 0' }}
          >
            {action.note.noteText}
          </p>
        </div>
      )}

      {action.recommendations && (
        <div>
          {action.recommendations.map((rec, i) => (
            <div key={i} className={styles.changeRow}>
              <span className={styles.changeField}>
                {rec.vendor} — {rec.category}
              </span>
              <span>{rec.rating}</span>
            </div>
          ))}
        </div>
      )}

      <div className={styles.buttonGroup}>
        <Button size="xs" onClick={handleConfirm} loading={isLoading}>
          {action.confirmLabel}
        </Button>
      </div>
    </div>
  );
}
